import {
    RESELLER_DEFAULT_COMMISSION_RATE,
    RESELLER_TERMS_VERSION,
} from '../data/resellerProgram';

const cleanString = (value) => (
    typeof value === 'string' ? value.trim() : ''
);

const nullableString = (value) => {
    const cleaned = cleanString(value);
    return cleaned || null;
};

const parsePositiveInteger = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

export const parseCommissionRate = (value, fallback = RESELLER_DEFAULT_COMMISSION_RATE) => {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) {
        return fallback;
    }

    return Math.min(100, Math.max(0, Number(parsed.toFixed(2))));
};

export const normalizeSocialLinks = (value) => {
    if (!value) {
        return [];
    }

    if (typeof value === 'string') {
        return value
            .split(/\r?\n|,/)
            .map((entry) => entry.trim())
            .filter(Boolean)
            .map((url, index) => ({
                id: `social-${index + 1}`,
                label: url.replace(/^https?:\/\//i, '').split(/[/?#]/)[0] || `Link ${index + 1}`,
                url,
            }));
    }

    if (Array.isArray(value)) {
        return value
            .map((entry, index) => {
                if (typeof entry === 'string') {
                    const url = entry.trim();
                    return url ? { id: `social-${index + 1}`, label: url, url } : null;
                }

                const url = cleanString(entry?.url || entry?.href || entry?.link);
                if (!url) {
                    return null;
                }

                return {
                    id: entry?.id || `social-${index + 1}`,
                    label: cleanString(entry?.label || entry?.name) || url,
                    url,
                };
            })
            .filter(Boolean);
    }

    return [];
};

export const sanitizeResellerCode = (value) => (
    cleanString(value)
        .toUpperCase()
        .replace(/[^A-Z0-9-]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 28)
);

export const buildResellerCode = ({
    preferredCode = '',
    businessName = '',
    applicantName = '',
    seed = Date.now(),
} = {}) => {
    const base = sanitizeResellerCode(preferredCode || businessName || applicantName) || 'PARTNER';
    const normalizedSeed = String(seed).toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(-6) || '000001';
    const withoutPrefix = base.replace(/^TJ-?/, '') || 'PARTNER';

    return sanitizeResellerCode(`TJ-${withoutPrefix}-${normalizedSeed}`);
};

export const serializeResellerApplication = (values = {}) => ({
    applicant_name: cleanString(values.applicantName || values.applicant_name),
    email: cleanString(values.email).toLowerCase(),
    phone: nullableString(values.phone),
    telegram_handle: nullableString(values.telegramHandle || values.telegram_handle),
    business_name: nullableString(values.businessName || values.business_name),
    website_url: nullableString(values.websiteUrl || values.website_url),
    country: nullableString(values.country),
    audience_type: nullableString(values.audienceType || values.audience_type),
    audience_size: nullableString(values.audienceSize || values.audience_size),
    sales_channels: nullableString(values.salesChannels || values.sales_channels),
    expected_monthly_referrals: parsePositiveInteger(values.expectedMonthlyReferrals ?? values.expected_monthly_referrals),
    preferred_code: nullableString(values.preferredCode || values.preferred_code),
    payment_method: nullableString(values.paymentMethod || values.payment_method),
    payment_account: nullableString(values.paymentAccount || values.payment_account),
    motivation: nullableString(values.motivation),
    marketing_plan: nullableString(values.marketingPlan || values.marketing_plan),
    social_links: normalizeSocialLinks(values.socialLinks || values.social_links),
    accepted_terms: Boolean(values.acceptedTerms || values.accepted_terms),
    terms_version: values.termsVersion || values.terms_version || RESELLER_TERMS_VERSION,
    metadata: {
        source: values.source || 'reseller_public_page',
        submittedLanguage: values.submittedLanguage || null,
    },
});

export const validateResellerApplication = (values = {}) => {
    const payload = serializeResellerApplication(values);
    const errors = {};

    if (!payload.applicant_name) {
        errors.applicantName = 'Name is required.';
    }

    if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
        errors.email = 'A valid email is required.';
    }

    if (!payload.audience_type) {
        errors.audienceType = 'Audience type is required.';
    }

    if (!payload.sales_channels) {
        errors.salesChannels = 'Sales channels are required.';
    }

    if (!payload.motivation) {
        errors.motivation = 'Motivation is required.';
    }

    if (!payload.accepted_terms) {
        errors.acceptedTerms = 'Terms must be accepted.';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
        payload,
    };
};

export const normalizeResellerApplication = (row = {}) => ({
    id: row.id,
    status: row.status || 'submitted',
    applicantName: row.applicantName || row.applicant_name || '',
    email: row.email || '',
    phone: row.phone || '',
    telegramHandle: row.telegramHandle || row.telegram_handle || '',
    businessName: row.businessName || row.business_name || '',
    websiteUrl: row.websiteUrl || row.website_url || '',
    country: row.country || '',
    audienceType: row.audienceType || row.audience_type || '',
    audienceSize: row.audienceSize || row.audience_size || '',
    salesChannels: row.salesChannels || row.sales_channels || '',
    expectedMonthlyReferrals: row.expectedMonthlyReferrals ?? row.expected_monthly_referrals ?? null,
    preferredCode: row.preferredCode || row.preferred_code || '',
    paymentMethod: row.paymentMethod || row.payment_method || '',
    paymentAccount: row.paymentAccount || row.payment_account || '',
    motivation: row.motivation || '',
    marketingPlan: row.marketingPlan || row.marketing_plan || '',
    socialLinks: normalizeSocialLinks(row.socialLinks || row.social_links),
    acceptedTerms: Boolean(row.acceptedTerms ?? row.accepted_terms),
    termsVersion: row.termsVersion || row.terms_version || '',
    resellerCode: row.resellerCode || row.reseller_code || '',
    commissionRate: row.commissionRate ?? row.commission_rate ?? null,
    reviewNotes: row.reviewNotes || row.review_notes || '',
    reviewedBy: row.reviewedBy || row.reviewed_by || null,
    reviewedAt: row.reviewedAt || row.reviewed_at || null,
    metadata: row.metadata || {},
    createdAt: row.createdAt || row.created_at || null,
    updatedAt: row.updatedAt || row.updated_at || null,
    raw: row,
});

export const normalizeResellerRecord = (row = {}) => ({
    id: row.id,
    applicationId: row.applicationId || row.application_id || null,
    userId: row.userId || row.user_id || null,
    code: row.code || '',
    displayName: row.displayName || row.display_name || '',
    email: row.email || '',
    phone: row.phone || '',
    telegramHandle: row.telegramHandle || row.telegram_handle || '',
    businessName: row.businessName || row.business_name || '',
    websiteUrl: row.websiteUrl || row.website_url || '',
    status: row.status || 'active',
    commissionRate: row.commissionRate ?? row.commission_rate ?? RESELLER_DEFAULT_COMMISSION_RATE,
    payoutMethod: row.payoutMethod || row.payout_method || '',
    payoutAccount: row.payoutAccount || row.payout_account || '',
    approvedBy: row.approvedBy || row.approved_by || null,
    approvedAt: row.approvedAt || row.approved_at || null,
    notes: row.notes || '',
    metadata: row.metadata || {},
    createdAt: row.createdAt || row.created_at || null,
    updatedAt: row.updatedAt || row.updated_at || null,
    raw: row,
});

export const normalizeResellerReferralRecord = (row = {}) => ({
    id: row.id,
    resellerId: row.resellerId || row.reseller_id || null,
    referralCode: row.referralCode || row.referral_code || '',
    userId: row.userId || row.user_id || null,
    userEmail: row.userEmail || row.user_email || '',
    sourceUrl: row.sourceUrl || row.source_url || '',
    status: row.status || 'lead',
    metadata: row.metadata || {},
    createdAt: row.createdAt || row.created_at || null,
    updatedAt: row.updatedAt || row.updated_at || null,
    raw: row,
});

export const normalizeResellerCommissionRecord = (row = {}) => ({
    id: row.id,
    resellerId: row.resellerId || row.reseller_id || null,
    referralId: row.referralId || row.referral_id || null,
    subscriptionOrderId: row.subscriptionOrderId || row.subscription_order_id || null,
    amount: Number(row.amount || 0),
    currency: row.currency || 'MMK',
    commissionRate: row.commissionRate ?? row.commission_rate ?? 0,
    status: row.status || 'pending',
    approvedBy: row.approvedBy || row.approved_by || null,
    approvedAt: row.approvedAt || row.approved_at || null,
    paidAt: row.paidAt || row.paid_at || null,
    notes: row.notes || '',
    metadata: row.metadata || {},
    createdAt: row.createdAt || row.created_at || null,
    updatedAt: row.updatedAt || row.updated_at || null,
    raw: row,
});

export const normalizeResellerPayoutRecord = (row = {}) => ({
    id: row.id,
    resellerId: row.resellerId || row.reseller_id || null,
    amount: Number(row.amount || 0),
    currency: row.currency || 'MMK',
    status: row.status || 'scheduled',
    paymentReference: row.paymentReference || row.payment_reference || '',
    notes: row.notes || '',
    paidBy: row.paidBy || row.paid_by || null,
    paidAt: row.paidAt || row.paid_at || null,
    metadata: row.metadata || {},
    createdAt: row.createdAt || row.created_at || null,
    updatedAt: row.updatedAt || row.updated_at || null,
    raw: row,
});

export const normalizeResellerDashboard = (payload = {}) => {
    const reseller = payload?.reseller ? normalizeResellerRecord(payload.reseller) : null;
    const stats = payload?.stats || {};

    return {
        reseller,
        referrals: (payload?.referrals || []).map(normalizeResellerReferralRecord),
        commissions: (payload?.commissions || []).map(normalizeResellerCommissionRecord),
        payouts: (payload?.payouts || []).map(normalizeResellerPayoutRecord),
        stats: {
            referralCount: Number(stats.referralCount || stats.referral_count || 0),
            paidReferralCount: Number(stats.paidReferralCount || stats.paid_referral_count || 0),
            pendingCommissionAmount: Number(stats.pendingCommissionAmount || stats.pending_commission_amount || 0),
            paidCommissionAmount: Number(stats.paidCommissionAmount || stats.paid_commission_amount || 0),
            scheduledPayoutAmount: Number(stats.scheduledPayoutAmount || stats.scheduled_payout_amount || 0),
            paidPayoutAmount: Number(stats.paidPayoutAmount || stats.paid_payout_amount || 0),
        },
        raw: payload,
    };
};

export const buildApprovedResellerPayload = ({
    application,
    code,
    commissionRate = RESELLER_DEFAULT_COMMISSION_RATE,
    actorId = null,
    notes = '',
} = {}) => {
    const source = application?.raw || application || {};
    const normalizedApplication = normalizeResellerApplication(source);
    const approvedAt = new Date().toISOString();

    return {
        application_id: normalizedApplication.id || null,
        code: sanitizeResellerCode(code) || buildResellerCode(normalizedApplication),
        display_name: normalizedApplication.businessName || normalizedApplication.applicantName,
        email: normalizedApplication.email || null,
        phone: normalizedApplication.phone || null,
        telegram_handle: normalizedApplication.telegramHandle || null,
        business_name: normalizedApplication.businessName || null,
        website_url: normalizedApplication.websiteUrl || null,
        status: 'active',
        commission_rate: parseCommissionRate(commissionRate),
        payout_method: normalizedApplication.paymentMethod || null,
        payout_account: normalizedApplication.paymentAccount || null,
        approved_by: actorId,
        approved_at: approvedAt,
        notes: nullableString(notes),
        metadata: {
            sourceApplicationStatus: normalizedApplication.status,
            audienceType: normalizedApplication.audienceType,
            audienceSize: normalizedApplication.audienceSize,
            expectedMonthlyReferrals: normalizedApplication.expectedMonthlyReferrals,
        },
    };
};
